import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Mail, RefreshCw, RotateCcw, UserRound } from 'lucide-react';
import { outgoingEmailsApi, getApiErrorMessage } from '../api';
import PageHeader from '../components/common/PageHeader';
import { LoadingState } from '../components/common/UI';
import { usePrompt } from '../store/ConfirmContext';
import { useAuth } from '../store/AuthContext';
import { formatEntityType } from '../utils/formatters';

const STATUS_LABELS = {
  queued: uiText("В очереди"),
  scheduled: uiText("Повторная попытка"),
  sent: uiText("Отправлено"),
  failed: uiText("Ошибка"),
  cancelled: uiText("Отменено — план изменён"),
};

const statusBadge = (status) => (
  status === 'sent' ? 'badge-green' : status === 'failed' ? 'badge-red' : status === 'scheduled' ? 'badge-orange' : status === 'cancelled' ? 'badge-gray' : 'badge-blue'
);

export default function OutgoingEmailsPage() {
  const prompt = usePrompt();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('mine');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = async (nextScope = scope) => {
    setLoading(true);
    setError('');
    try {
      const res = await outgoingEmailsApi.list(undefined, nextScope);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load('mine'); }, []);

  const visibleItems = useMemo(
    () => (status ? items.filter((item) => item.status === status) : items),
    [items, status],
  );

  const stats = useMemo(() => ({
    total: items.length,
    queued: items.filter((item) => ['queued', 'scheduled'].includes(item.status)).length,
    sent: items.filter((item) => item.status === 'sent').length,
    failed: items.filter((item) => item.status === 'failed').length,
  }), [items]);

  const run = async (id, operation, successMessage) => {
    setBusyId(id);
    setError('');
    setNotice('');
    try {
      await operation();
      setNotice(successMessage);
      await load(scope);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const markFailed = async (id) => {
    const reason = await prompt(uiText("Укажите причину ошибки отправки."), {
      title: uiText("Ошибка отправки"),
      defaultValue: 'SMTP недоступен',
      confirmLabel: uiText("Сохранить"),
      required: true,
    });
    if (reason === null) return;
    await run(id, () => outgoingEmailsApi.markFailed(id, reason.trim() || 'Ошибка отправки'), uiText("Статус письма обновлён."));
  };

  const changeScope = (nextScope) => {
    setScope(nextScope);
    load(nextScope);
  };

  return (
    <div className="workspace-page outgoing-emails-page">
      <PageHeader
        icon={Mail}
        title={uiText("Сообщения")}
        subtitle={scope === 'all' ? uiText("Полный журнал системных сообщений, отправленных пользователям.") : uiText("Ваши сообщения, сформированные и отправленные из Audit Pro.")}
        right={(
          <button className="btn btn-outline" type="button" onClick={() => load(scope)} disabled={loading}>
            <RefreshCw size={15} />{uiText(" Обновить")}</button>
        )}
      />

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      {isAdmin ? (
        <div className="email-hub-scope" role="group" aria-label={uiText("Область журнала сообщений")}>
          <button type="button" className={scope === 'mine' ? 'active' : ''} onClick={() => changeScope('mine')}><UserRound size={15} />{uiText(" Мои сообщения")}</button>
          <button type="button" className={scope === 'all' ? 'active' : ''} onClick={() => changeScope('all')}><Mail size={15} />{uiText(" Все сообщения системы")}</button>
        </div>
      ) : null}

      <div className="workspace-stats-grid email-hub-stats">
        <div className="workspace-stat"><div className="workspace-stat-value">{stats.total}</div><div className="workspace-stat-label">{uiText("Всего")}</div></div>
        <div className="workspace-stat"><div className="workspace-stat-value">{stats.queued}</div><div className="workspace-stat-label">{uiText("Ожидают отправки")}</div></div>
        <div className="workspace-stat"><div className="workspace-stat-value">{stats.sent}</div><div className="workspace-stat-label">{uiText("Отправлено")}</div></div>
        <div className="workspace-stat"><div className="workspace-stat-value">{stats.failed}</div><div className="workspace-stat-label">{uiText("Требуют внимания")}</div></div>
      </div>

      <div className="card outgoing-emails-toolbar">
        <div className="card-body">
          <label className="email-hub-filter">
            <span>{uiText("Показать")}</span>
            <select className="form-control" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">{uiText("Все сообщения")}</option>
              <option value="queued">{uiText("В очереди")}</option>
              <option value="scheduled">{uiText("Повторная попытка")}</option>
              <option value="sent">{uiText("Отправленные")}</option>
              <option value="failed">{uiText("С ошибкой")}</option>
              <option value="cancelled">{uiText("Отменённые")}</option>
            </select>
          </label>
          <div className="workspace-muted">{uiText("Показано ")}{visibleItems.length}{uiText(" из ")}{items.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">{uiText("История сообщений")}</div></div>
        <div className="card-body">
          {loading ? <LoadingState message={uiText("Загружаем письма…")} /> : visibleItems.length === 0 ? (
            <div className="workspace-empty-state">
              <Mail size={24} />
              <div className="workspace-empty-state-title">{uiText("Сообщений пока нет")}</div>
              <div className="workspace-empty-state-text">{uiText("Здесь появятся сообщения, которые система отправит из карточек компаний, аудитов и документов.")}</div>
            </div>
          ) : (
            <div className="outgoing-email-list">
              {visibleItems.map((item) => (
                <article key={item.id} className="outgoing-email-item">
                  <div className="outgoing-email-head">
                    <div>
                      <div className="outgoing-email-subject">{item.subject}</div>
                      <div className="workspace-muted">{uiText("Кому: ")}{item.to_email} · {formatEntityType(item.entity_type)} #{item.entity_id || '—'}</div>
                      {scope === 'all' ? <div className="workspace-muted">{uiText("Отправитель: ")}{item.sender_name || uiText("Система")}</div> : null}
                    </div>
                    <span className={`badge ${statusBadge(item.status)}`}>{STATUS_LABELS[item.status] || item.status}</span>
                  </div>
                  <div className="outgoing-email-body">{item.body}</div>
                  <div className="outgoing-email-foot">
                    <div className="workspace-muted">{uiText("Создано: ")}{item.created_at ? new Date(item.created_at).toLocaleString('ru-RU') : '—'}
                      {item.sent_at ? uiText(" · Отправлено: {v0}", {v0: (new Date(item.sent_at).toLocaleString('ru-RU'))}) : ''}
                      {item.resend_of_id ? uiText(" · Повтор письма #{v0}", {v0: (item.resend_of_id)}) : ''}
                      {item.error ? uiText(" · Ошибка: {v0}", {v0: (item.error)}) : ''}
                    </div>
                    <div className="outgoing-email-actions">
                      {item.can_resend && item.status !== 'cancelled' ? (
                        <button className="btn btn-outline btn-sm" type="button" disabled={busyId === item.id} onClick={() => run(item.id, () => outgoingEmailsApi.resend(item.id), uiText("Повторная отправка запущена. Дополнительных действий не требуется."))}><RotateCcw size={13} /> {item.status === 'failed' ? uiText("Повторить отправку") : uiText("Отправить повторно")}</button>
                      ) : null}
                      {['queued', 'scheduled'].includes(item.status) ? <span className="workspace-muted" role="status">{uiText("Отправка выполняется автоматически")}</span> : null}
                      {item.can_manage && item.status !== 'sent' ? <button className="btn btn-outline btn-sm" type="button" disabled={busyId === item.id} onClick={() => run(item.id, () => outgoingEmailsApi.markSent(item.id), uiText("Письмо отмечено отправленным."))}><CheckCircle2 size={13} />{uiText(" Отметить отправленным")}</button> : null}
                      {item.can_manage && item.status !== 'failed' ? <button className="btn btn-danger btn-sm" type="button" disabled={busyId === item.id} onClick={() => markFailed(item.id)}><AlertCircle size={13} />{uiText(" Отметить ошибкой")}</button> : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
